/**
 * The measure popup — `docs/property-maps-v2.md` §5.4, board 3.
 *
 * Anchored to the shape on the tape, like the shape popup is to a shape on a
 * layer — but it asks different questions, because what it is about to make
 * is a *measurement* rather than a drawing: which type it counts towards, and
 * what units to read it in.
 *
 * **Units are display only.** The number is stored in the type's own unit and
 * `ac` or `sq yd` is a way of looking at it, remembered per browser — a
 * company that prices by the square foot does not stop doing so because
 * somebody found acres easier to read on a snow lot.
 *
 * **There is no "use on a quote".** Not here and nowhere in this feature
 * (§12 rule 5): a quote reads property measurements, so *Add to property
 * measurements* is the one path and the builder's Takeoffs list is the other
 * end of it.
 */
import { useEffect, useState } from 'react'
import { convertArea, formatLength, measuresOf, unitFamily, type AreaUnit } from '@rowted/shared'
import { Button, Segmented } from '../../components/primitives.js'
import { SelectInput, TextInput } from '../../components/form/inputs.js'
import { cn } from '../../lib/cn.js'
import { formatNumber } from '../../lib/format.js'
import { IconRuler } from '../../frame/icons.js'
import type { MeasureShape } from '../measure-session.js'
import type { MeasurementType } from '../api.js'

/** How somebody prefers to read an area, per browser (§5.4). */
const UNIT_KEY = 'rowted.measure.units'

const AREA_UNITS: { value: AreaUnit; label: string }[] = [
  { value: 'sq ft', label: 'sq ft' },
  { value: 'sq yd', label: 'sq yd' },
  { value: 'ac', label: 'ac' },
]

function readUnit(): AreaUnit {
  try {
    const saved = window.localStorage.getItem(UNIT_KEY)
    return AREA_UNITS.some((one) => one.value === saved) ? (saved as AreaUnit) : 'sq ft'
  } catch {
    return 'sq ft'
  }
}

export function MeasurePopup({
  shape, types, who, at, canEdit, sheet, onChange, onDiscard, onAdd,
}: {
  shape: MeasureShape
  /** The catalogue's types, which is what Type offers. */
  types: MeasurementType[]
  who: string | null
  at: { x: number; y: number }
  canEdit: boolean
  /** Drawn in a bottom sheet rather than anchored to the outline (§6.3). */
  sheet?: boolean
  onChange: (patch: Partial<MeasureShape>) => void
  onDiscard: () => void
  /** Opens the review dialog — §5.4's save is always reviewed. */
  onAdd: () => void
}) {
  const [unit, setUnit] = useState<AreaUnit>(readUnit)

  useEffect(() => {
    try { window.localStorage.setItem(UNIT_KEY, unit) } catch { /* private window */ }
  }, [unit])

  const { areaSqft, lengthFt } = measuresOf(shape.kind, shape.geometry)
  /* A line measures a length and an area measures both, so the Type list is
     narrowed to what this shape can actually answer. */
  const family = areaSqft === null ? 'length' : 'area'
  const offered = types.filter((one) => unitFamily(one.unit) === family)

  return (
    <div
      {...(sheet ? {} : { style: { left: at.x, top: at.y } })}
      className={cn(
        sheet && 'w-full',
        !sheet && 'absolute z-20 w-[310px] overflow-hidden rounded-popup border border-line',
        !sheet && 'bg-surface shadow-popover',
      )}
    >
      <div className="flex items-center gap-2 border-b border-line-faint px-3 py-2.5">
        <span className="inline-flex text-accent"><IconRuler size={14} /></span>
        <b className="text-cell">Measuring</b>
        <span className={cn(
          'ml-auto inline-flex h-[19px] items-center rounded-tag bg-warn-bg px-1.5 text-sub',
          'font-semibold text-warn',
        )}
        >
          not saved
        </span>
      </div>

      <div className="flex flex-col gap-2 px-3 py-2.5 text-[12px]">
        <div className="flex gap-3.5">
          {areaSqft !== null && (
            <span>
              <span className="text-text-muted">Area</span>
              <b className="ml-1.5 tabular-nums">
                {`${formatNumber(convertArea(areaSqft, unit))} ${unit}`}
              </b>
            </span>
          )}
          {lengthFt !== null && (
            <span>
              <span className="text-text-muted">
                {areaSqft === null ? 'Length' : 'Perimeter'}
              </span>
              <b className="ml-1.5 tabular-nums">{formatLength(lengthFt)}</b>
            </span>
          )}
        </div>

        {areaSqft !== null && (
          <label className="flex items-center gap-2">
            <span className="w-11 shrink-0 text-text-muted">Units</span>
            <Segmented
              options={AREA_UNITS}
              value={unit}
              onChange={setUnit}
              label="Units"
            />
          </label>
        )}

        <label className="flex items-center gap-2">
          <span className="w-11 shrink-0 text-text-muted">Name</span>
          <TextInput
            value={shape.name}
            disabled={!canEdit}
            placeholder="Front lawn"
            onChange={(event) => onChange({ name: event.target.value })}
          />
        </label>

        <label className="flex items-center gap-2">
          <span className="w-11 shrink-0 text-text-muted">Type</span>
          <SelectInput
            label="Type"
            value={shape.typeId ?? ''}
            disabled={!canEdit}
            onChange={(value) => onChange({ typeId: value })}
            options={offered.map((one) => ({
              value: one.measurementTypeId, label: `${one.name} · ${one.unit}`,
            }))}
          />
        </label>

        {/* Read-only, because it is a fact rather than a choice: a shape
            traced over the imagery was measured off an aerial, by you, now. */}
        <div className="flex items-center gap-2">
          <span className="w-11 shrink-0 text-text-muted">Source</span>
          <span className="truncate text-text-body">
            {`Aerial${who ? ` · ${who}` : ''} · today`}
          </span>
        </div>
      </div>

      <div className="flex gap-1.5 border-t border-line-faint px-3 py-2.5">
        <Button size="toolbar" disabled={!canEdit} onClick={onDiscard}>
          Discard
        </Button>
        <Button
          variant="primary"
          size="toolbar"
          className="ml-auto"
          disabled={!canEdit || !shape.typeId || shape.name.trim() === ''}
          onClick={onAdd}
        >
          Add to property measurements
        </Button>
      </div>
    </div>
  )
}
